import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaComments, FaTimes, FaPaperPlane, FaRobot, FaUser } from 'react-icons/fa';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const ChatbotContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const ChatbotButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00ccff, #39ff14);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  box-shadow: 0 4px 20px rgba(0, 204, 255, 0.4);
  transition: all 0.3s ease;
  animation: ${float} 3s ease-in-out infinite;
  
  &:hover {
    animation: ${pulse} 0.5s ease-in-out;
    box-shadow: 0 6px 25px rgba(0, 204, 255, 0.6);
  }
  
  &.has-notification {
    &::after {
      content: '';
      position: absolute;
      top: -2px;
      right: -2px;
      width: 12px;
      height: 12px;
      background: #ff0080;
      border-radius: 50%;
      border: 2px solid white;
    }
  }
`;

const ChatWindow = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 350px;
  height: 500px;
  background: #11162b;
  border: 2px solid rgba(0, 204, 255, 0.3);
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideUp} 0.3s ease-out;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    border: none;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #00ccff, #39ff14);
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .title {
    color: white;
    font-weight: 600;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .status {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 5px;
    
    &::before {
      content: '●';
      color: #39ff14;
      font-size: 0.6rem;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 204, 255, 0.5);
    border-radius: 3px;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  animation: ${slideUp} 0.3s ease-out;
  
  &.user {
    flex-direction: row-reverse;
    
    .message-content {
      background: linear-gradient(135deg, #00ccff, #39ff14);
      color: white;
      margin-left: 40px;
    }
  }
  
  &.bot {
    .message-content {
      background: rgba(255, 255, 255, 0.1);
      color: #b0eaff;
      margin-right: 40px;
    }
  }
`;

const MessageAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
  
  &.user {
    background: linear-gradient(135deg, #00ccff, #39ff14);
    color: white;
  }
  
  &.bot {
    background: rgba(0, 204, 255, 0.2);
    color: #00ccff;
  }
`;

const MessageContent = styled.div`
  padding: 12px 16px;
  border-radius: 18px;
  max-width: 80%;
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 0.9rem;
`;

const QuickSuggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const SuggestionButton = styled.button`
  background: rgba(0, 204, 255, 0.1);
  border: 1px solid rgba(0, 204, 255, 0.3);
  color: #00ccff;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 204, 255, 0.2);
    border-color: #00ccff;
  }
`;

const ChatInput = styled.div`
  padding: 15px 20px;
  border-top: 1px solid rgba(0, 204, 255, 0.2);
  display: flex;
  gap: 10px;
  align-items: center;
`;

const InputField = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(0, 204, 255, 0.3);
  border-radius: 20px;
  padding: 10px 15px;
  color: #b0eaff;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;
  
  &:focus {
    border-color: #00ccff;
  }
  
  &::placeholder {
    color: rgba(176, 234, 255, 0.6);
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #00ccff, #39ff14);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #b0eaff;
  font-size: 0.8rem;
  font-style: italic;
  
  .dots {
    display: flex;
    gap: 3px;
    
    span {
      width: 4px;
      height: 4px;
      background: #00ccff;
      border-radius: 50%;
      animation: ${pulse} 1.4s ease-in-out infinite both;
      
      &:nth-child(1) { animation-delay: -0.32s; }
      &:nth-child(2) { animation-delay: -0.16s; }
      &:nth-child(3) { animation-delay: 0; }
    }
  }
`;

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState('session-' + Date.now());
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      setTimeout(() => {
        addBotMessage(
          "Hi there! 🎪 Welcome to InflatableTx! I'm your AI assistant. How can I help you with your bounce house rental today?",
          ['I want to rent a bounce house', 'What do you have available?', 'How much does it cost?', 'Do you deliver to my area?']
        );
      }, 500);
    }
  }, [isOpen]);

  const addBotMessage = (text, suggestions = []) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      text,
      suggestions,
      timestamp: new Date()
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      text,
      timestamp: new Date()
    }]);
  };

  const getBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Simple pattern matching for demo
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return {
        text: "Hello! 👋 Thanks for reaching out! I'm here to help you find the perfect bounce house for your event. What can I do for you?",
        suggestions: ['Show me bounce houses', 'Get pricing info', 'Check availability', 'Delivery information']
      };
    }
    
    if (message.includes('price') || message.includes('cost') || message.includes('how much')) {
      return {
        text: "Our bounce house rentals start at $199 for a full day! 💰 Prices vary based on size, features, and date. Would you like a custom quote for your event?",
        suggestions: ['Yes, get me a quote', 'What affects pricing?', 'Any discounts available?', 'Compare options']
      };
    }
    
    if (message.includes('bounce house') || message.includes('available') || message.includes('what do you have')) {
      return {
        text: "We have amazing bounce houses! 🏰 Popular options include princess castles, pirate ships, water slides, and combo units. What age group and theme interests you?",
        suggestions: ['Princess castles', 'Water slides', 'Pirate themes', 'Combo units']
      };
    }
    
    if (message.includes('delivery') || message.includes('setup') || message.includes('area')) {
      return {
        text: "We deliver throughout the Dallas-Fort Worth area! 🚚 Setup is included and we typically arrive 2-4 hours before your event. Where is your event located?",
        suggestions: ['Dallas area', 'Fort Worth area', 'Surrounding cities', 'Setup details']
      };
    }
    
    if (message.includes('book') || message.includes('rent') || message.includes('reserve')) {
      return {
        text: "Great! I'd love to help you book a bounce house! 🎉 What date is your event and how many guests are you expecting?",
        suggestions: ['This weekend', 'Next month', 'Birthday party', 'School event']
      };
    }
    
    if (message.includes('safety') || message.includes('clean') || message.includes('insurance')) {
      return {
        text: "Safety is our top priority! 🛡️ All equipment is professionally cleaned, inspected, and fully insured. We provide safety guidelines with every rental.",
        suggestions: ['Cleaning process', 'Safety guidelines', 'Insurance details', 'Age restrictions']
      };
    }
    
    // Default response
    return {
      text: "I'd be happy to help! 🤔 Could you tell me more about what you're looking for? I can help with bookings, pricing, availability, or any questions about our bounce houses!",
      suggestions: ['View bounce houses', 'Get pricing', 'Check availability', 'Call us: (682) 202-5624']
    };
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    addUserMessage(userMessage);
    
    // Show typing indicator
    setIsTyping(true);
    
    // Simulate API delay
    setTimeout(() => {
      const response = getBotResponse(userMessage);
      setIsTyping(false);
      addBotMessage(response.text, response.suggestions);
    }, 1000 + Math.random() * 1000);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setTimeout(() => handleSendMessage(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <ChatbotContainer>
      {!isOpen && (
        <ChatbotButton 
          onClick={() => setIsOpen(true)}
          className={messages.length > 0 ? 'has-notification' : ''}
        >
          <FaComments />
        </ChatbotButton>
      )}
      
      {isOpen && (
        <ChatWindow>
          <ChatHeader>
            <div>
              <div className="title">
                <FaRobot />
                InflatableTx Assistant
              </div>
              <div className="status">Online - Typically replies instantly</div>
            </div>
            <CloseButton onClick={() => setIsOpen(false)}>
              <FaTimes />
            </CloseButton>
          </ChatHeader>
          
          <ChatMessages>
            {messages.map((message) => (
              <Message key={message.id} className={message.type}>
                <MessageAvatar className={message.type}>
                  {message.type === 'user' ? <FaUser /> : <FaRobot />}
                </MessageAvatar>
                <div>
                  <MessageContent className="message-content">
                    {message.text}
                  </MessageContent>
                  {message.suggestions && message.suggestions.length > 0 && (
                    <QuickSuggestions>
                      {message.suggestions.map((suggestion, index) => (
                        <SuggestionButton
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </SuggestionButton>
                      ))}
                    </QuickSuggestions>
                  )}
                </div>
              </Message>
            ))}
            
            {isTyping && (
              <Message className="bot">
                <MessageAvatar className="bot">
                  <FaRobot />
                </MessageAvatar>
                <MessageContent className="message-content">
                  <TypingIndicator>
                    AI is typing
                    <div className="dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </TypingIndicator>
                </MessageContent>
              </Message>
            )}
            
            <div ref={messagesEndRef} />
          </ChatMessages>
          
          <ChatInput>
            <InputField
              type="text"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isTyping}
            />
            <SendButton 
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
            >
              <FaPaperPlane />
            </SendButton>
          </ChatInput>
        </ChatWindow>
      )}
    </ChatbotContainer>
  );
};

export default ChatbotWidget;
